import React, { useState, useEffect, useRef } from "react";
import { useEventListener } from "usehooks-ts";
import { InputProps } from "./types";
import { useFormContext } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import Label from "./label";
import styles from "./styles";

type SuggestionInputProps = InputProps & {
    options: SelectOption[]
}

interface SelectOption {
    value: string,
    label: string
}

export const SuggestionInput = (props: SuggestionInputProps) => {
    const {
        name,
        label,
        cssName,
        labelCssName,
        fieldOptions = {},
        type = "text",
        placeHolder,
        options
    } = props;

    const inputRef = useRef<HTMLInputElement>(null);

    const _name = name.toString();

    const { register, setValue, getValues, formState: { errors } } = useFormContext();

    const registerHook = register(name.toString(), fieldOptions);

    const currentValue = () => getValues(_name).toString();

    const [showDataList, setShowDataList] = useState(false);
    const [_options, setOptions] = useState<SelectOption[] | null | undefined>([]);

    useEffect(() => {
        setOptions(options);

        const value = currentValue().toLowerCase();

        const currentOption = options.find(x => x.value.toLowerCase() === value);

        if (currentOption == null) {
            return;
        }

        updateOption(currentOption.value, currentOption.label);
    }, []);

    const checkBlur = (e: any) => {
        const input = document.getElementById(name);

        if (e.target !== input) {
            setShowDataList(false);
        }
    }

    useEventListener("click", checkBlur);

    const arrowClick = (e: any) => {
        e.stopPropagation();
        e.preventDefault();

        setShowDataList(x => !x);
    }

    const inputClick = () => {
        setShowDataList(true);
    }

    const updateOption = (value: string, label: string) => {
        setValue(_name, value);

        if (inputRef.current === null) {
            return;
        }

        const input: HTMLInputElement = inputRef.current as HTMLInputElement;
        input.value = label;
    }

    const optionClick = (e: any) => {
        updateOption(e.target.value, e.target.dataset.label);

        arrowClick(e);
    }

    const inputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setShowDataList(true);

        const text = (e.target.value ?? "").toLowerCase();

        if (!text) {
            setOptions(options);
            updateOption("", "");
            return;
        }

        const filteredOptions = options.filter(x => x.label.toLowerCase().includes(text))

        setOptions(filteredOptions);
    }

    const noResultsOption = (<option className="list-no-results" value="">No matching results</option>);

    //Not really using DataList 
    //https://www.w3schools.com/tags/tryit.asp?filename=tryhtml5_datalist

    const searchOption = (option: SelectOption, index: number) =>
        <option
            className={option.value === currentValue() ? "active" : ""}
            onClick={optionClick} key={index}
            value={option.value}
            data-label={option.label}
        >
            {option.label}
        </option>

    return (
        <div className={cssName}>
            <input
                id={name}
                type={type}
                className={styles.elements.input}
                placeholder={placeHolder ?? label}
                //defaultValue={inputText} //https://www.w3schools.com/jsref/prop_text_defaultvalue.asp
                onChange={inputChange}
                onBlur={registerHook.onBlur}
                onClick={inputClick}
                autoComplete="off"
                ref={inputRef} />

            <Label htmlFor={_name} text={label} cssName={labelCssName} />

            <i onClick={arrowClick} className={`select-arrow ${showDataList ? "list-open" : ""}`}></i>

            <datalist className={showDataList ? "list-show" : "list-hide"}>
                {_options?.length
                    ? _options?.map(searchOption)
                    : noResultsOption
                }
            </datalist>

            <ErrorMessage
                errors={errors}
                name={_name}
                render={({ message }) => <p className="error">{message}</p>}
            />
        </div>
    );
}

export default SuggestionInput;